// Import necessary libraries and components
import { Box, Chip, Grid, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  getFacilities,
  getFacilitiesError,
  getFacilitiesLoading,
} from '../../pages/slices/facility.slice'
import {
  getDicomLogsError,
  getDicomLogsLoading,
  getSelectedLog,
} from '../../pages/slices/dicomLog.slice'
import { CodeBlock } from '../common/CodeBlock/CodeBlock'
import CopyToClipboard from '../common/CopyToClipboard/CopyToClipboard'
import Loader from '../common/Loader/Loader'
import Tabs from '../common/Tabs/Tabs'
import {
  formatDateTime,
  formatTimeRemaining,
} from '../../pages/HL7historyLogs/utils'

const DicomLogDetail = ({ logId }) => {
  // Selectors for Redux state
  const loading = useSelector(getDicomLogsLoading)
  const error = useSelector(getDicomLogsError)
  const dicomLog = useSelector((state) => getSelectedLog(state, logId))
  const facilities = useSelector(getFacilities)
  const isFacilitiesLoading = useSelector(getFacilitiesLoading)
  const isFacilitiesError = useSelector(getFacilitiesError)

  // Find facility details based on kpro_team_id
  const facility = facilities?.find(
    (facility) => facility?.kpro_team_id === dicomLog?.kproTeamId
  )
  const facilityName = facility?.facility_name || 'N/A'
  const facilityTeamId = facility?.kpro_team_id || 'N/A'

  // Configuration for tabs
  const tabsConfig = useMemo(
    () => [
      {
        id: 0,
        label: <Typography>Dicom Sender Logs</Typography>,
        content: (
          <CodeBlock
            noDataMessage={dicomLog?.error ? '' : 'No Dicom error Available'}
          >
            {dicomLog?.error}
          </CodeBlock>
        ),
      },
    ],
    [dicomLog?.error]
  )

  // Render loader if data is loading
  if (loading || isFacilitiesLoading) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: '50vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader />
      </Box>
    )
  }

  // Render error message if there is an error
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error fetching log details: {error.message || 'Unknown error'}
        </Typography>
      </Box>
    )
  }

  const NOT_AVAILABLE = 'Not Available'

  // Main render for DICOM Log Details
  return (
    <Box sx={{ height: '80vh', overflowY: 'auto' }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h4" fontWeight={600}>
              Order number: {dicomLog?.orderNumber || NOT_AVAILABLE}
            </Typography>
            <CopyToClipboard
              text={dicomLog?.orderNumber || ''}
              disabled={!!dicomLog?.orderNumber}
            />
          </Box>
        </Grid>

        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            Facility Name
          </Typography>
          {isFacilitiesError ? (
            <Typography variant="body1" color="error">
              Error fetching team:{' '}
              {isFacilitiesError?.message || 'Unknown error'}
            </Typography>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1">{facilityName}</Typography>
              {facilityName && (
                <CopyToClipboard
                  menuOptions={[
                    {
                      label: 'Copy Facility Name',
                      value: facilityName,
                    },
                    {
                      label: 'Copy Facility ID',
                      value: facilityTeamId,
                    },
                  ]}
                />
              )}
            </Box>
          )}
        </Grid>

        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            Event ID
          </Typography>
          <Typography variant="body1">
            {dicomLog?.eventId || NOT_AVAILABLE}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            Created At
          </Typography>
          <Typography variant="body1">
            {dicomLog?.createdAt
              ? formatDateTime(dicomLog?.createdAt)
              : NOT_AVAILABLE}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            MRN
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body1">
              {dicomLog?.mrn || NOT_AVAILABLE}
            </Typography>
            <CopyToClipboard
              text={dicomLog?.mrn || ''}
              disabled={!!dicomLog?.mrn}
            />
          </Box>
        </Grid>

        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            Retry Count
          </Typography>
          <Typography variant="body1">
            {dicomLog?.retryCount || NOT_AVAILABLE}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            Status
          </Typography>
          <Chip
            size="small"
            label={dicomLog?.status === 0 ? 'Failed' : 'Success'}
            color={dicomLog?.status === 0 ? 'error' : 'success'}
          />
        </Grid>

        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            Recorded At
          </Typography>
          <Typography variant="body1">
            {dicomLog?.recordedAt
              ? formatDateTime(dicomLog?.recordedAt)
              : NOT_AVAILABLE}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            Sent At
          </Typography>
          <Typography variant="body1">
            {dicomLog?.sentAt
              ? formatDateTime(dicomLog?.sentAt)
              : NOT_AVAILABLE}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <Typography variant="subtitle2" color="textSecondary">
            Processing Time
          </Typography>
          <Typography variant="body1">
            {dicomLog?.sentAt && dicomLog?.recordedAt
              ? formatTimeRemaining(dicomLog?.recordedAt, dicomLog?.sentAt)
              : NOT_AVAILABLE}
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Tabs tabsConfig={tabsConfig} />
      </Box>
    </Box>
  )
}

export default DicomLogDetail
